import {
  ListEntity,
  usersKeys,
  UserListEntity,
  UsersQuery,
  AxiosAPIError,
  UserValidator,
  UserUpdateValidator,
  HostEntity,
  profileKeys,
  LookEntity,
  UserOwnershipValidator,
  UserOwnershipEntity,
  PaginationQuery,
  ownershipsKeys,
  userHostRolesKeys,
  PaginationQueryV2,
  ListEntityV2,
  UpdatePasswordValidator,
  FanGroupBySlug,
  ResetPasswordValidator,
  UserRole,
  RequestAccessStatus,
  approverKeys,
  fansKeys,
  FanCustomInfo,
  routes,
  FanGroupCustomInformation,
  fanGroupsKeys,
  SearchType,
  LOCAL_STORAGE_SLUG,
} from '@seaters/constants';

import {
  QueryClient,
  UseMutationResult,
  useMutation,
  useQuery,
  useQueryClient,
  useInfiniteQuery,
  keepPreviousData,
} from '@tanstack/react-query';

import * as Sentry from '@sentry/react';

import {
  fetchFGBySlug,
  fetchHost,
  fetchLook,
  resetUserPassword,
  updatePassword,
} from '../api';
import {
  fetchAdminUsers,
  fetchUser,
  deleteUser,
  cancelUserScheduledDeletion,
  updateUser,
  createUser,
  createUserOwnership,
  fetchUserOwnerships,
  fetchUserHostRoles,
  createUserHostRole,
  unlockUser,
  fetchUserRolesForCurrentFG,
  fetchUserGroups,
  fetchApproverGroups,
  fetchUserCustomInfo,
  addFanCustomInfo,
  FanCustomInfoBody,
  deleteUserOwnership,
  deleteUserHostRole,
  deleteUserPhone,
} from '../api/users';
import { getSessionToken } from '../storage';
import { notification } from 'antd';
import { t } from 'i18next';
import {
  isVersionConflict,
  invalidateVersionedQueries,
  noteVersionedSiblingWrite,
  readVersionedSiblingWrite,
  useVersionedSnapshot,
  withExpectedVersion,
} from '../utils/versionedUpdate';

// The endpoints below write the user aggregate outside optimistic locking, so its @Version
// advances behind any editor holding a snapshot — record it or that editor's next PUT 409s.
// From the refreshed cache, never the write's own body: handlers map their view pre-flush.
const noteUserVersionFromCache = (client: QueryClient, userId: string) =>
  noteVersionedSiblingWrite(
    client,
    userId,
    client.getQueryData<UserListEntity>(usersKeys.detail(userId))?.version
  );

// Two shapes, one rule: note the version off whatever refresh the hook already does, never a
// second fetch. This one is for writes with no response body. `exact` matters: `detail(id)` is a
// key PREFIX of `fanGroups(id)` and `companies(id)`, which a non-exact refetch would drag in.
const refreshUserAndNoteVersion = async (
  client: QueryClient,
  userId: string
) => {
  await client.invalidateQueries({
    queryKey: usersKeys.detail(userId),
    exact: true,
    refetchType: 'all',
  });
  noteUserVersionFromCache(client, userId);
};

// For writes that answer with the updated user: the cache is primed from the body for an instant
// render, and the version still comes from the refetch this invalidation triggers.
const primeUserAndNoteVersion = async (
  client: QueryClient,
  userId: string,
  user: UserListEntity
) => {
  client.setQueryData(usersKeys.detail(userId), user);
  await client.invalidateQueries({ queryKey: usersKeys.all() });
  noteUserVersionFromCache(client, userId);
};

export const useFetchAdminUsers = (
  params: UsersQuery,
  enabled: boolean = true
) =>
  useQuery<ListEntity<UserListEntity>, Error>({
    queryKey: usersKeys.list(params),
    queryFn: () => fetchAdminUsers(params),
    placeholderData: keepPreviousData,
    enabled,
  });

export const useFetchAdminUsersInfinite = (
  query: string,
  searchType: SearchType
) =>
  useInfiniteQuery({
    queryKey: usersKeys.list({ query, searchType }),
    initialPageParam: {
      maxPageSize: 10,
      itemOffset: 0,
      query,
      searchType,
    },
    queryFn: ({ pageParam }) => fetchAdminUsers(pageParam),
    getNextPageParam: ({ totalSize, itemOffset, maxPageSize }) => {
      const nextOffset = itemOffset + maxPageSize;

      return totalSize - itemOffset > maxPageSize
        ? {
            itemOffset: nextOffset,
            maxPageSize: maxPageSize,
            query,
            searchType,
          }
        : undefined;
    },
    placeholderData: keepPreviousData,
  });

export const useFetchHost = () =>
  useQuery<HostEntity, Error>({
    queryKey: usersKeys.host(),
    queryFn: () => fetchHost(),
    refetchOnMount: false,
  });

export const useFetchLook = (slug: string) => {
  const isEnabled = !!slug && !Object.values(routes).includes(slug);
  return useQuery<
    LookEntity,
    Error & { response: { data: { message: string } } }
  >({
    queryKey: usersKeys.look(slug),
    queryFn: async () => {
      try {
        return await fetchLook(slug);
      } catch (error) {
        // useFetchLook fires for the first URL segment on every route, so a 404
        // just means that segment isn't a fan group (a UUID, a stale/typo'd slug,
        // or a non-fan-group path like /docs). The app degrades gracefully, so a
        // 404 here is an expected outcome, not an error worth reporting — without
        // this, the default queryErrorHandler reports every such 404 to Sentry
        // (the fan-group-slug 404 "grab-bag"). Everything else still surfaces,
        // mirroring that default handler.
        const status = Number(
          (error as { response?: { status?: number } }).response?.status
        );
        if ([400, 403].includes(status) || status >= 500) {
          Sentry.captureException(error, {
            extra: { stack: error instanceof Error ? error.stack : undefined },
          });
        }
        throw error;
      }
    },
    // refetchOnMount: false,
    // refetchOnReconnect: false,
    enabled: isEnabled,
    // staleTime: 1000 * 60 * 5,
    retry: 0,
  });
};

export const useFetchFGBySlug = (slug: string, shouldRefetch?: boolean) => {
  const auth = getSessionToken();

  return useQuery<
    FanGroupBySlug,
    Error & { response: { data: { message: string } } }
  >({
    queryKey: usersKeys.fanGroupBySlug(slug),
    queryFn: async () => {
      try {
        return await fetchFGBySlug(slug);
      } catch (error) {
        if (
          (error as { response?: { status?: number } }).response?.status === 404
        ) {
          localStorage.removeItem(LOCAL_STORAGE_SLUG);
        }
        throw error;
      }
    },
    enabled: !!slug && !!auth,
    refetchInterval: !shouldRefetch
      ? false
      : (query) => {
          if (
            query.state.data?.membership?.request?.status !==
            RequestAccessStatus.PENDING
          ) {
            return false;
          }
          return 1000;
        },
    retry: 0,
  });
};

export const useResetPassword = (): UseMutationResult<
  void,
  AxiosAPIError<ResetPasswordValidator>,
  ResetPasswordValidator,
  unknown
> => {
  return useMutation({
    mutationFn: (body: ResetPasswordValidator) => resetUserPassword(body),
  });
};

export const useUpdatePassword = (): UseMutationResult<
  void,
  AxiosAPIError<UpdatePasswordValidator>,
  UpdatePasswordValidator,
  unknown
> => {
  return useMutation({
    mutationFn: (body: UpdatePasswordValidator) => updatePassword(body),
  });
};

export const useFetchUser = (userId: string) =>
  useQuery<UserListEntity, Error>({
    queryKey: usersKeys.detail(userId),
    queryFn: () => fetchUser(userId),
    enabled: !!userId,
  });

export const useFetchUserFanGroups = (userId: string) =>
  useQuery<ListEntityV2<FanGroupBySlug>, Error>({
    queryKey: usersKeys.fanGroups(userId),
    queryFn: () => fetchUserGroups(userId),
    enabled: !!userId,
  });

export const useFetchApproverUserFanGroups = (userId: string) =>
  useQuery<ListEntityV2<UserOwnershipEntity>, Error>({
    queryKey: approverKeys.fanGroups(userId),
    queryFn: () => fetchApproverGroups(userId),
    enabled: !!userId,
  });

export const useFetchUserRolesForCurrentFG = (fanGroupId: string) => {
  const auth = getSessionToken();

  return useQuery<{ roles: UserRole[] }, Error>({
    queryKey: profileKeys.fanGroupRoles(fanGroupId),
    queryFn: () => fetchUserRolesForCurrentFG(fanGroupId),
    // Privileged read: with no token the interceptor sends no bearer, so
    // this can only 403. Gated like `useFetchFGBySlug` above. #2124.
    enabled: !!fanGroupId && !!auth,
  });
};

export const useFetchUserOwnerships = (
  userId: string,
  params: PaginationQuery
) =>
  useQuery<ListEntity<UserOwnershipEntity>, Error>({
    queryKey: ownershipsKeys.forUser(userId, params),
    queryFn: () => fetchUserOwnerships(userId, params),
    enabled: !!userId,
  });

export const useFetchUserHostRoles = (
  userId: string,
  params: PaginationQueryV2
) =>
  useQuery<ListEntityV2<UserOwnershipEntity>, Error>({
    queryKey: userHostRolesKeys.forUser(userId, params),
    queryFn: () => fetchUserHostRoles(userId, params),
    enabled: !!userId,
  });

export const useCreateUser = (): UseMutationResult<
  UserListEntity,
  AxiosAPIError<UserValidator>,
  UserValidator,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: UserValidator) => createUser(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usersKeys.all() });
    },
  });
};

export const useCreateUserOwnership = (): UseMutationResult<
  ResponseType,
  AxiosAPIError<UserOwnershipValidator>,
  UserOwnershipValidator,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ownershipsKeys.mutation(),
    mutationFn: (body: UserOwnershipValidator) => createUserOwnership(body),
    // Granting the ownership can add the FGO role, which bumps the user aggregate.
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ownershipsKeys.all() });
      queryClient.invalidateQueries({
        queryKey: fanGroupsKeys.fanGroupOwner(),
      });
      return refreshUserAndNoteVersion(queryClient, userId);
    },
  });
};

export const useDeleteUserOwnership = (): UseMutationResult<
  ResponseType,
  AxiosAPIError<UserOwnershipValidator>,
  UserOwnershipValidator,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ownershipsKeys.mutation(),
    mutationFn: (body: UserOwnershipValidator) => deleteUserOwnership(body),
    onSuccess: () => {
      notification.success({
        message: t('notification_success_ownership_deleted'),
      });
      queryClient.invalidateQueries({ queryKey: ownershipsKeys.all() });
      queryClient.invalidateQueries({
        queryKey: fanGroupsKeys.fanGroupOwner(),
      });
    },
  });
};

export const useCreateUserHostRole = (): UseMutationResult<
  ResponseType,
  AxiosAPIError<UserOwnershipValidator>,
  UserOwnershipValidator,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: userHostRolesKeys.mutation(),
    mutationFn: (body: UserOwnershipValidator) => createUserHostRole(body),
    // Granting the host role can add it to the user's roles, which bumps the aggregate.
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: userHostRolesKeys.all() });
      // A host-role change is reflected in every company's user list and the
      // call sites carry no companyId, so refresh all company-users queries.
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === 'companies' && query.queryKey.includes('users'),
      });
      return refreshUserAndNoteVersion(queryClient, userId);
    },
  });
};

export const useDeleteUserHostRole = (): UseMutationResult<
  ResponseType,
  AxiosAPIError<UserOwnershipValidator>,
  UserOwnershipValidator,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: userHostRolesKeys.mutation(),
    mutationFn: (body: UserOwnershipValidator) => deleteUserHostRole(body),
    onSuccess: () => {
      notification.success({
        message: t('notification_success_host_role_deleted'),
      });
      queryClient.invalidateQueries({ queryKey: userHostRolesKeys.all() });
      queryClient.invalidateQueries({ queryKey: fanGroupsKeys.host() });
      // A host-role change is reflected in every company's user list and the
      // call sites carry no companyId, so refresh all company-users queries.
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === 'companies' && query.queryKey.includes('users'),
      });
    },
  });
};

export interface UpdateUserOptions {
  /**
   * Whether the editor's fields currently show the refreshed user. Only then may a Save adopt the
   * version a sibling write produced: adopting one the fields do not reflect turns a conflict with
   * another admin into a silent overwrite. Editors that never re-hydrate leave this out.
   */
  isFormSynchronized?: () => boolean;
}

export const useUpdateUser = (
  userId: string,
  snapshotVersion?: number,
  { isFormSynchronized }: UpdateUserOptions = {}
): UseMutationResult<
  UserListEntity,
  AxiosAPIError<UserUpdateValidator>,
  UserUpdateValidator,
  unknown
> => {
  const queryClient = useQueryClient();
  const cachedVersion = queryClient.getQueryData<UserListEntity>(
    usersKeys.detail(userId)
  )?.version;
  // Releasing the phone, unlocking and cancelling a scheduled deletion all write this
  // aggregate from the same screens, each bumping the version outside optimistic locking.
  const { acceptServerVersion, getExpectedVersion } = useVersionedSnapshot(
    userId,
    snapshotVersion ?? cachedVersion,
    () =>
      isFormSynchronized?.()
        ? readVersionedSiblingWrite(queryClient, userId)
        : undefined
  );

  return useMutation({
    mutationKey: usersKeys.mutation(),
    mutationFn: (body: UserUpdateValidator) =>
      updateUser(userId, withExpectedVersion(body, getExpectedVersion())),
    onSuccess: (updatedUser) => {
      acceptServerVersion(updatedUser);
      queryClient.setQueryData(usersKeys.detail(userId), updatedUser);
    },
    onError: async (error) => {
      if (isVersionConflict(error)) {
        await invalidateVersionedQueries(queryClient, usersKeys.detail(userId));
      }
    },
  });
};

export const useUnlockUser = (
  userId: string
): UseMutationResult<
  unknown,
  AxiosAPIError<{ userId: string }>,
  string,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: usersKeys.mutation(),
    mutationFn: () => unlockUser(userId),
    // No body: the version has to come back from the refreshed detail query.
    onSuccess: () => refreshUserAndNoteVersion(queryClient, userId),
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId }: { userId: string }) => deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usersKeys.all() });
    },
  });
};

export const useDeleteUserPhone = (userId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => deleteUserPhone(userId),
    onSuccess: (user) => primeUserAndNoteVersion(queryClient, userId, user),
  });
};

export const useCancelUserScheduledDeletion = (userId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => cancelUserScheduledDeletion(userId),
    onSuccess: (user) => primeUserAndNoteVersion(queryClient, userId, user),
  });
};

export const useUserCustomInfo = (fanGroupId: string, userId: string) => {
  return useQuery<ListEntityV2<FanCustomInfo>, Error>({
    queryKey: fansKeys.customInfo(userId),
    queryFn: () => fetchUserCustomInfo(fanGroupId, userId),
  });
};

export const useAddFanCustomInfo = (
  fanGroupId: string,
  userId: string
): UseMutationResult<
  FanGroupCustomInformation,
  AxiosAPIError<{
    body: FanCustomInfoBody;
  }>,
  {
    body: FanCustomInfoBody;
  },
  string
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ body }: { body: FanCustomInfoBody }) =>
      addFanCustomInfo(fanGroupId, userId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fansKeys.customInfo(userId) });
    },
  });
};
